import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Copy } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const MatrixMultiplication = () => {
  const [matrixA, setMatrixA] = useState([[1, 2], [3, 4]]);
  const [matrixB, setMatrixB] = useState([[5, 6], [7, 8]]);
  const [result, setResult] = useState<number[][] | null>(null);
  const [sizeA, setSizeA] = useState({ rows: 2, cols: 2 });
  const [sizeB, setSizeB] = useState({ rows: 2, cols: 2 });
  const [showSteps, setShowSteps] = useState(false);
  const { toast } = useToast();

  const initializeMatrix = (rows: number, cols: number): number[][] => {
    return Array.from({ length: rows }, () =>
      Array.from({ length: cols }, () => 0)
    );
  };


  const updateMatrixSize = (matrix: 'A' | 'B', dimension: 'rows' | 'cols', value: number) => {
    const newValue = Math.max(1, Math.min(10, value)); // Ensure value is between 1 and 10
    
    if (matrix === 'A') {
      setSizeA(prevSize => {
        const newSize = { ...prevSize, [dimension]: newValue };
        
        // Update the matrix with the new size
        setMatrixA(prevMatrix => {
          // Create a new matrix with the new size
          const newMatrix = [];
          let counter = 1; // Start counter for new values
          
          // First, find the maximum value in the existing matrix to continue the sequence
          if (prevMatrix.length > 0) {
            const flatMatrix = prevMatrix.flat();
            counter = Math.max(...flatMatrix) + 1;
            if (!isFinite(counter)) counter = 1; // In case all values are 0
          }
          
          // Copy existing rows up to the new row count
          for (let i = 0; i < newSize.rows; i++) {
            if (i < prevMatrix.length) {
              // Copy existing row
              const row = [...prevMatrix[i]];
              // Add new columns with incremented values if needed
              while (row.length < newSize.cols) {
                row.push(counter++);
              }
              // Trim if columns were reduced
              if (row.length > newSize.cols) {
                row.length = newSize.cols;
              }
              newMatrix.push(row);
            } else {
              // Add new row with incremented values
              newMatrix.push(Array.from(
                { length: newSize.cols },
                (_, j) => counter++
              ));
            }
          }
          
          return newMatrix;
        });
        
        return newSize;
      });
    } else {
      setSizeB(prevSize => {
        const newSize = { ...prevSize, [dimension]: newValue };
        
        // Update the matrix with the new size
        setMatrixB(prevMatrix => {
          // Create a new matrix with the new size
          const newMatrix = [];
          let counter = 1; // Start counter for new values
          
          // First, find the maximum value in the existing matrix to continue the sequence
          if (prevMatrix.length > 0) {
            const flatMatrix = prevMatrix.flat();
            counter = Math.max(...flatMatrix) + 1;
            if (!isFinite(counter)) counter = 1; // In case all values are 0
          }
          
          // Copy existing rows up to the new row count
          for (let i = 0; i < newSize.rows; i++) {
            if (i < prevMatrix.length) {
              // Copy existing row
              const row = [...prevMatrix[i]];
              // Add new columns with incremented values if needed
              while (row.length < newSize.cols) {
                row.push(counter++);
              }
              // Trim if columns were reduced
              if (row.length > newSize.cols) {
                row.length = newSize.cols;
              }
              newMatrix.push(row);
            } else {
              // Add new row with incremented values
              newMatrix.push(Array.from(
                { length: newSize.cols },
                (_, j) => counter++
              ));
            }
          }
          
          return newMatrix;
        });
        
        return newSize;
      });
    }
    setResult(null);
  };

  const updateMatrixValue = (matrix: 'A' | 'B', row: number, col: number, value: string) => {
    const numValue = parseFloat(value) || 0;
    if (matrix === 'A') {
      setMatrixA(prevMatrix => {
        const newMatrix = prevMatrix.map(r => [...r]);
        newMatrix[row][col] = numValue;
        return newMatrix;
      });
    } else {
      setMatrixB(prevMatrix => {
        const newMatrix = prevMatrix.map(r => [...r]);
        newMatrix[row][col] = numValue;
        return newMatrix;
      });
    }
  };

  const multiplyMatrices = () => {
    if (sizeA.cols !== sizeB.rows) {
      alert('Jumlah kolom matriks A harus sama dengan jumlah baris matriks B!');
      return;
    }

    const result = initializeMatrix(sizeA.rows, sizeB.cols);
    
    for (let i = 0; i < sizeA.rows; i++) {
      for (let j = 0; j < sizeB.cols; j++) {
        let sum = 0;
        for (let k = 0; k < sizeA.cols; k++) {
          sum += matrixA[i][k] * matrixB[k][j];
        }
        result[i][j] = sum;
      }
    }

    setResult(result);
  };

  const canMultiply = sizeA.cols === sizeB.rows;

  const copyResult = async () => {
    if (!result) return;
    
    const resultText = result.map(row => row.join(' ')).join('\n');
    try {
      await navigator.clipboard.writeText(resultText);
      toast({
        title: "Berhasil!",
        description: "Hasil perhitungan telah disalin ke clipboard",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Gagal menyalin hasil",
        variant: "destructive",
      });
    }
  };

  const copySteps = async () => {
    if (!result) return;
    
    let stepsText = "Langkah Perhitungan Matriks:\n\n";
    result.forEach((row, i) => {
      row.forEach((cell, j) => {
        stepsText += `Hasil[${i}][${j}] = ${cell}\n`;
        stepsText += `= ${Array.from({ length: sizeA.cols }, (_, k) => 
          `${matrixA[i][k]} × ${matrixB[k][j]}`
        ).join(' + ')}\n`;
        stepsText += `= ${Array.from({ length: sizeA.cols }, (_, k) => 
          matrixA[i][k] * matrixB[k][j]
        ).join(' + ')}\n\n`;
      });
    });
    
    try {
      await navigator.clipboard.writeText(stepsText);
      toast({
        title: "Berhasil!",
        description: "Langkah perhitungan telah disalin ke clipboard",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Gagal menyalin langkah perhitungan",
        variant: "destructive",
      });
    }
  };

  const MatrixDisplay = ({ matrix, label, size, editable = true }: {
    matrix: number[][];
    label: string;
    size: { rows: number; cols: number };
    editable?: boolean;
  }) => (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Matriks {label}</CardTitle>
        {editable && (
          <div className="flex gap-4">
            <div className="flex items-center gap-2">
              <Label>Baris:</Label>
              <Input
                type="number"
                min="1"
                max="5"
                value={size.rows}
                onChange={(e) => updateMatrixSize(label as 'A' | 'B', 'rows', parseInt(e.target.value) || 1)}
                className="w-16"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label>Kolom:</Label>
              <Input
                type="number"
                min="1"
                max="5"
                value={size.cols}
                onChange={(e) => updateMatrixSize(label as 'A' | 'B', 'cols', parseInt(e.target.value) || 1)}
                className="w-16"
              />
            </div>
          </div>
        )}
      </CardHeader>
      <CardContent>
        <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${size.cols}, 1fr)` }}>
          {matrix.map((row, i) =>
            row.map((cell, j) => (
              <Input
                key={`${i}-${j}`}
                type="number"
                value={cell}
                onChange={(e) => editable && updateMatrixValue(label as 'A' | 'B', i, j, e.target.value)}
                className="w-16 h-12 text-center"
                readOnly={!editable}
              />
            ))
          )}
        </div>
        <div className="text-center mt-2 text-sm text-muted-foreground">
          {size.rows} × {size.cols}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Perkalian Matriks</CardTitle>
          <CardDescription>
            Kalikan dua matriks dan lihat proses perhitungannya
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <MatrixDisplay matrix={matrixA} label="A" size={sizeA} />
            <MatrixDisplay matrix={matrixB} label="B" size={sizeB} />
          </div>

          <div className="mt-6 flex items-center justify-center gap-4">
            <Button 
              onClick={multiplyMatrices} 
              disabled={!canMultiply}
              className="px-8"
            >
              A × B
            </Button>
            {!canMultiply && (
              <div className="text-sm text-destructive">
                Kolom A ({sizeA.cols}) ≠ Baris B ({sizeB.rows})
              </div>
            )}
          </div>

          {result && (
            <div className="mt-6 space-y-4">
              <div className="flex justify-center">
                <MatrixDisplay 
                  matrix={result} 
                  label="Hasil (A × B)" 
                  size={{ rows: sizeA.rows, cols: sizeB.cols }}
                  editable={false}
                />
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    Langkah Perhitungan
                    <div className="flex gap-2">
                      <Button 
                        onClick={copySteps}
                        variant="outline"
                        size="sm"
                      >
                        <Copy className="w-4 h-4 mr-1" />
                        Salin
                      </Button>
                      <Button 
                        onClick={() => setShowSteps(!showSteps)}
                        variant="outline"
                        size="sm"
                      >
                        {showSteps ? 'Sembunyikan' : 'Tampilkan'}
                      </Button>
                    </div>
                  </CardTitle>
                </CardHeader>
                {showSteps && (
                  <CardContent>
                    <div className="space-y-3 text-sm font-mono">
                      {result.map((row, i) =>
                        row.map((cell, j) => (
                          <div key={`${i}-${j}`} className="p-2 bg-muted rounded">
                            <div className="font-bold">Hasil[{i}][{j}] = {cell}</div>
                            <div className="text-muted-foreground">
                              = {Array.from({ length: sizeA.cols }, (_, k) => 
                                `${matrixA[i][k]} × ${matrixB[k][j]}`
                              ).join(' + ')}
                            </div>
                            <div className="text-muted-foreground">
                              = {Array.from({ length: sizeA.cols }, (_, k) => 
                                matrixA[i][k] * matrixB[k][j]
                              ).join(' + ')}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                )}
              </Card>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MatrixMultiplication;